import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  CoachStrategyProposalCard,
  createProposalCommentPrefill,
  getPendingStrategyProposals,
} from '../CoachStrategyProposalCard';

/**
 * Approval controls for proposals generated from today's daily insight.
 * Coach Hub remains the full proposal surface; this is the inline Today
 * affordance promised by the insight card.
 *
 * Diagnostics contract: every proposal action logs a start line, an outcome
 * line, and (on failure) the surfaced error. A failing action must never look
 * like a no-op, so the error is also rendered in the card.
 */

export const PROPOSAL_LOG_PREFIX = '[DailyInsightProposal]';

export const logProposalEvent = (event, detail = {}, logger = console) => {
  const payload = { event, at: new Date().toISOString(), ...detail };
  if (event.endsWith('_failed')) logger.error(`${PROPOSAL_LOG_PREFIX} ${event}`, payload);
  else logger.info(`${PROPOSAL_LOG_PREFIX} ${event}`, payload);
  return payload;
};

export const getRecommendationRefreshKey = (recommendation) => [
  recommendation?._id || recommendation?.id || '',
  recommendation?.updated_at || recommendation?.created_at || ''
].join(':');

const DailyInsightProposal = ({ backendService, recommendation, isToday }) => {
  const [proposals, setProposals] = useState([]);
  const [reviewOpen, setReviewOpen] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [actionError, setActionError] = useState(null);
  const recommendationRefreshKey = getRecommendationRefreshKey(recommendation);
  const previousPendingIds = useRef(new Set());

  useEffect(() => {
    const pendingIds = new Set(proposals.map(item => String(item.id || item._id)));
    // Surface newly arrived suggestions without undoing a deliberate collapse
    // when the same pending records are refreshed, reordered, or removed.
    if ([...pendingIds].some(id => !previousPendingIds.current.has(id))) setReviewOpen(true);
    previousPendingIds.current = pendingIds;
  }, [proposals]);

  const refresh = useCallback(async () => {
    if (!backendService?.fetchStrategyProposals) {
      logProposalEvent('refresh_skipped', { reason: 'no_backend_service' });
      return [];
    }
    const visible = await backendService.fetchStrategyProposals();
    const pending = getPendingStrategyProposals({ proposals: visible });
    logProposalEvent('refresh_completed', {
      fetched: Array.isArray(visible) ? visible.length : 0,
      pending: pending.length,
      pendingIds: pending.map((item) => String(item.id || item._id)),
      statuses: (Array.isArray(visible) ? visible : []).map((item) => item?.status)
    });
    setProposals(pending);
    return pending;
  }, [backendService]);

  useEffect(() => {
    if (!isToday) return undefined;
    refresh().catch((error) => {
      logProposalEvent('refresh_failed', { message: error?.message, stack: error?.stack });
    });
    return undefined;
  }, [isToday, refresh, recommendationRefreshKey]);

  if (!isToday || proposals.length === 0) return null;

  const respond = async (proposal, action, comment) => {
    const id = proposal?.id || proposal?._id;
    if (!id) {
      logProposalEvent('action_skipped', { reason: 'missing_proposal_id', action, proposalKeys: Object.keys(proposal || {}) });
      return;
    }
    if (busyId) {
      logProposalEvent('action_skipped', { reason: 'action_already_in_flight', action, requestedId: String(id), busyId });
      return;
    }
    logProposalEvent('action_started', {
      action,
      proposalId: String(id),
      kind: proposal?.kind,
      scope: proposal?.scope,
      status: proposal?.status,
      recommendationId: recommendation?._id ? String(recommendation._id) : null,
      hasComment: Boolean(comment)
    });
    setBusyId(String(id));
    setActionError(null);
    const startedAt = Date.now();
    try {
      let result;
      if (action === 'approve') result = await backendService.approveStrategyProposal(id);
      if (action === 'reject') result = await backendService.rejectStrategyProposal(id, comment);
      if (action === 'acknowledge') result = await backendService.acknowledgeStrategyProposal(id);
      if (action === 'comment') result = await backendService.commentOnStrategyProposal(id, comment);
      logProposalEvent('action_succeeded', {
        action,
        proposalId: String(id),
        durationMs: Date.now() - startedAt,
        resultStatus: result?.status || null,
        adjustments: result?.references?.adjustments || null,
        updatedFutureActivityIds: result?.references?.updated_future_activity_ids || null,
        skippedFutureActivities: result?.references?.skipped_future_activities || null,
        unresolvedExerciseNames: result?.references?.unresolved_exercise_names || null
      });
      const pending = await refresh();
      if (action !== 'comment' && pending.some((item) => String(item.id || item._id) === String(id))) {
        logProposalEvent('action_state_inconsistent', {
          action,
          proposalId: String(id),
          detail: 'proposal is still pending after a successful action response'
        });
      }
    } catch (error) {
      logProposalEvent('action_failed', {
        action,
        proposalId: String(id),
        durationMs: Date.now() - startedAt,
        message: error?.message,
        stack: error?.stack
      });
      setActionError(error?.message || `Could not ${action} this proposal. Please try again.`);
    } finally {
      setBusyId(null);
    }
  };

  const comment = (proposal) => {
    const value = window.prompt('Comment on this proposal:', createProposalCommentPrefill(proposal));
    if (value?.trim()) respond(proposal, 'comment', value.trim());
  };

  return (
    <div className="daily-insight-proposal" onClick={(event) => event.stopPropagation()}>
      <button type="button" className="today-proposal-disclosure" aria-expanded={reviewOpen} onClick={() => setReviewOpen(open => !open)}>Changes to review ({proposals.length}) · {reviewOpen ? 'Close review' : 'Review'}</button>
      {reviewOpen && proposals.map((proposal) => (
        <CoachStrategyProposalCard
          key={proposal.id || proposal._id}
          proposal={proposal}
          onApprove={(item) => respond(item, 'approve')}
          onDismiss={(item) => respond(item, 'reject')}
          onAcknowledge={(item) => respond(item, 'acknowledge')}
          onComment={comment}
          disabled={busyId === String(proposal.id || proposal._id)}
        />
      ))}
      {actionError && (
        <p className="daily-insight-proposal__error" role="alert">{actionError}</p>
      )}
    </div>
  );
};

export default DailyInsightProposal;
